import React from 'react';
import { pdf } from '@react-pdf/renderer';
import DocumentFormPdfDocument from '../components/DocumentFormPdfDocument';

/**
 * 미리보기 DOM → PNG 데이터 URL 페이지 배열 (DocumentFormPreviewArea / PdfViewerPopup 와 동일 파이프라인)
 * - `.dfb-preview-page`가 있으면 페이지별 캡처, 없으면 root 한 덩어리
 * - 팝업 등 스크롤 영역은 클론에서 max-height 해제해 전체 본문 캡처
 */
export async function captureElementToPdfImageDataUrls(rootEl) {
  if (!rootEl) {
    throw new Error('미리보기 영역을 찾지 못했습니다.');
  }

  const { default: html2canvas } = await import('html2canvas');

  const pageEls = [...(rootEl.querySelectorAll?.('.dfb-preview-page') || [])];
  const targets = pageEls.length > 0 ? pageEls : [rootEl];

  if (pageEls.length > 0) {
    document.body.classList.add('dfb-capture-doc');
  }

  const html2canvasOptions = {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    onclone: (clonedDoc) => {
      clonedDoc.querySelectorAll('.form-preview-doc-body').forEach((n) => {
        n.style.maxHeight = 'none';
        n.style.overflow = 'visible';
      });
    },
  };

  try {
    const pages = [];
    const A4_RATIO = 297 / 210;

    for (const target of targets) {
      const canvas = await html2canvas(target, html2canvasOptions);
      const pageHeightPx = Math.max(1, Math.floor(canvas.width * A4_RATIO));

      for (let y = 0; y < canvas.height; y += pageHeightPx) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - y);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = pageHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        }
        pages.push(sliceCanvas.toDataURL('image/png'));
      }
    }

    return pages;
  } finally {
    if (pageEls.length > 0) {
      document.body.classList.remove('dfb-capture-doc');
    }
  }
}

function buildPdfFilename(base) {
  let s = String(base || 'document').trim() || 'document';
  s = s.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
  if (!/\.pdf$/i.test(s)) s += '.pdf';
  return s;
}

/** `dataJson.layoutRows`의 각 행 객체를 한 맵으로 합쳐 PDF 파일명용 값 조회 */
export function mergeDataJsonRowsFlat(layoutRows) {
  const out = {};
  if (!layoutRows || typeof layoutRows !== 'object') return out;
  for (const rows of Object.values(layoutRows)) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      for (const [k, v] of Object.entries(row)) {
        if (v == null || v === '') continue;
        if (typeof v === 'object' && !Array.isArray(v)) {
          for (const [nk, nv] of Object.entries(v)) {
            if (nv == null || nv === '') continue;
            if (out[nk] === undefined || out[nk] === '') out[nk] = nv;
          }
          continue;
        }
        if (out[k] === undefined || out[k] === '') out[k] = v;
      }
    }
  }
  return out;
}

function firstNonEmptyString(flat, keys) {
  for (const key of keys) {
    const v = flat[key];
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

function sanitizeFilenameSegment(s) {
  return String(s || '')
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * 회원가입 신청서 등에서 기관·기업 유형인지 (`userSignupReportPreview` / 관리자 상세와 동일 기준)
 * @param {string} [authorCd]
 */
export function isOrganizationSignupAuthorType(authorCd) {
  if (authorCd == null || String(authorCd).trim() === '') return false;
  const a = String(authorCd).toLowerCase();
  if (a === 'individual' || a === 'individual_user_info') return false;
  return (
    a === 'company_user_info' ||
    a === 'company' ||
    a === 'company_org_info' ||
    a.includes('gov') ||
    a.includes('inst') ||
    a.includes('region') ||
    a.includes('expert_org')
  );
}

/**
 * formPreview PDF 저장 시 기본 파일명(확장자 제외).
 * - `pdfDownloadFileName`이 있으면 그대로 사용
 * - 문서 미리보기(`documentFormPreview` + `layoutRows`)인 경우: 개인 유형은 이름_, 기관 유형은 기관명_ 접두사
 */
export function resolveFormPreviewPdfFileBaseName(cfg) {
  const explicit = cfg?.pdfDownloadFileName != null ? String(cfg.pdfDownloadFileName).trim() : '';
  if (explicit) return explicit.replace(/\.pdf$/i, '');

  const titleTrim = String(cfg?.title || '')
    .trim()
    .replace(/\s*보기\s*$/i, '')
    .replace(/\s*미리보기\s*$/i, '')
    .trim();
  const suffix =
    titleTrim ||
    String(cfg?.structureJson?.title || '').trim() ||
    String(cfg?.documentTitle || '가입신청서').trim() ||
    'document';

  const layoutRows = cfg?.dataJson?.layoutRows;
  const shouldPrefixSignupName =
    cfg?.documentFormPreview && layoutRows && typeof layoutRows === 'object';

  if (!shouldPrefixSignupName) {
    return suffix || 'document';
  }

  const authorRaw = cfg.signupAuthorCd ?? cfg.formType;
  const isOrg = isOrganizationSignupAuthorType(authorRaw);
  const flat = mergeDataJsonRowsFlat(layoutRows);
  const meta = cfg.signupPdfMeta || {};

  const PERSON_KEYS = [
    'user_nm',
    'userNm',
    'user_name',
    'userName',
    'name',
    'applicant_name',
    'applicantName',
    'member_name',
    'memberName',
    'kor_nm',
    'korNm',
  ];
  const ORG_KEYS = [
    'company_name',
    'companyName',
    'org_name',
    'orgName',
    'organization_name',
    'organizationName',
    'institution_name',
    'institutionName',
    'affiliation',
    'org_title',
    'orgTitle',
  ];

  const prefixRaw = isOrg
    ? String(meta.orgDisplayName || '').trim() || firstNonEmptyString(flat, ORG_KEYS)
    : String(meta.userDisplayName || '').trim() || firstNonEmptyString(flat, PERSON_KEYS);

  const prefix = sanitizeFilenameSegment(prefixRaw);
  if (prefix) {
    const suf = sanitizeFilenameSegment(suffix);
    return suf ? `${prefix}_${suf}` : prefix;
  }

  return suffix || 'document';
}

/**
 * @param {HTMLElement} rootEl
 * @param {string} [filenameBase] - 확장자 없이 또는 .pdf 포함
 */
export async function downloadFormPreviewPdf(rootEl, filenameBase = 'document') {
  const images = await captureElementToPdfImageDataUrls(rootEl);
  const blob = await pdf(<DocumentFormPdfDocument images={images} />).toBlob();
  const url = URL.createObjectURL(blob);
  const name = buildPdfFilename(filenameBase);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
